import { useEffect, useMemo, useState } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';

import { getAgencyOfficials, getOfficial } from '../../../networkRequest/officialsService';
import { EventService } from '../../../networkRequest/EventService';
import { updateOfficialVerification } from '../utils/data';

import { useUser } from '@hooks/useUser';
import { useAgency } from '@hooks/useAgency';
import useBatchedPagination from '@hooks/useBatchedPagination';
import { UseRowEditCalculator } from '@hooks/dataGridEditing/UseRowEditCalculator';
import { useRowEdit } from '@hooks/dataGridEditing/UseRowEdit';

import { exportFilteredDataToCSV } from '../utils/csvExport';
import { getSelectedRowIds, toRowSelectionModel } from '../../../utils/gridSelection';
import { agencyVerificationcColumns, OfficialWithSingleVerification } from './TableConfigs';
import { formatVerifications } from '../utils/verificationHelpers';
import Toast from '@components/Toast';
import VerificationsToolBar from './VerificationsToolBar';

const AgencyVerificationsTable = () => {
  const { user } = useUser();
  const { agency } = useAgency();

  const apiRef = useGridApiRef();

  const [rowSelectionModel, setRowSelectionModel] = useState<readonly (string | number)[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const {
    data: rawOfficials,
    loading: officialsLoading,
    error: officialsError,
    setError: setOfficialsError,
    updateItem: updateOfficialInData,
  } = useBatchedPagination(
    user?.accessToken ?? '',
    getAgencyOfficials,
    { params: { agencyId: agency?.id } }
  );

  // Format the officials data to have single verifications
  const officials: OfficialWithSingleVerification[] = useMemo(() => {
    if (!rawOfficials) return [];
    return formatVerifications(rawOfficials);
  }, [rawOfficials]);

  const calculateRowEdit = UseRowEditCalculator();
  const {
    processRowUpdate,
    handleProcessRowUpdateError,
    editPromiseInfo,
    setEditPromiseInfo,
    resetRowMutations,
    rowMutations,
    loading: editLoading,
    error: editError
  } = useRowEdit(calculateRowEdit);

  const allLoading = officialsLoading || editLoading;

  const handleExport = () => {
    setExportLoading(true);
    try {
      if (user === null) {
        throw new Error('user is null');
      }
      exportFilteredDataToCSV(apiRef, user?.profile?.agency?.name);
      EventService.recordVerificationsCsvDownload(user.accessToken, user?.profile.agency_id);
    } catch (error) {
      setOfficialsError('Failed to export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const saveSingleVerificationEdit = async (
    officialId: number,
  ) => {
    // Update the official in the batched pagination data
    const updatedOfficial = rawOfficials.find((official) => official.id === officialId);
    if (updatedOfficial) {
      const { data: official } = await getOfficial(user?.accessToken ?? '', updatedOfficial.id)
      if (official) {
        const updatedData = {
          ...updatedOfficial,
          ...official,
        };
        updateOfficialInData(updatedData);
      }
    }
  };

  useEffect(() => {
    const updateData = async () => {
      if (!user) {
        setOfficialsError('No User Found');
        return;
      }
      // when the grid processes an edit and updates editPromiseInfo, we can trigger the networkrequest to save the update.
      if (editPromiseInfo && rowMutations) {
        const { originalRow, resolve } = editPromiseInfo;
        try {
          if (
            editPromiseInfo.updatedRow.agency_verifications ===
            editPromiseInfo.originalRow.agency_verifications?.status
          ) {
            resolve(originalRow);
            return;
          }

          const verificationId = originalRow.agency_verifications?.id;

          if (!verificationId) {
            throw new Error(
              'Cannot update verification: no verification record exists for this official.'
            );
          }

          const updatedData = {
            verification: {
              status: rowMutations.updatedValue as string
            },
            id: verificationId
          };

          const data = await updateOfficialVerification(user, updatedData);
          if (data) {
            saveSingleVerificationEdit(originalRow.id);
            setSuccessMessage(
              `Updated Official ${originalRow.first_name} ${originalRow.last_name} to ${rowMutations.updatedLabel} successfully.`
            );
            const fullRowData = {
              ...editPromiseInfo.originalRow,
              // for local officials state, each official only has the most recent verification saved, not an array.
              agency_verifications: { ...data }
            };
            resolve(fullRowData);
          }

          resetRowMutations();
          setEditPromiseInfo(null);
        } catch (error: any) {
          setOfficialsError(error.message);
          resolve(originalRow);
        }
      }
    };
    updateData();
  }, [
    apiRef,
    editPromiseInfo,
    resetRowMutations,
    rowMutations,
    setEditPromiseInfo,
    user,
    setOfficialsError
  ]);

  const hasAnyError = editError || officialsError;
  const errorMessages = [officialsError, editError];

  return (
    <>
      {allLoading ? null : (
        <>
          {hasAnyError && (
            <Toast type="error" onClose={() => setOfficialsError('')}>
              {errorMessages.filter((msg) => msg !== '').join('\n')}
            </Toast>
          )}
          {successMessage && (
            <Toast type="success" onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Toast>
          )}
        </>
      )}
      <DataGrid
        columns={agencyVerificationcColumns}
        rows={officials}
        loading={allLoading}
        disableRowSelectionOnClick
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        apiRef={apiRef}
        rowSelectionModel={toRowSelectionModel(rowSelectionModel)}
        onRowSelectionModelChange={(newSelection) => {
          setRowSelectionModel(getSelectedRowIds(newSelection, apiRef));
        }}
        localeText={{
          toolbarColumns: 'Manage Columns'
        }}
        showToolbar
        slots={{
          toolbar: () => (
            <VerificationsToolBar
              onExport={handleExport}
              exportLoading={exportLoading}
            />
          )
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton'
          }
        }}
        sx={{
          marginTop: '16px',
          '--DataGrid-overlayHeight': '300px',
          height: '100%',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '600'
          },
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: '#E3F2FD'
          },
          '& .MuiDataGrid-columnHeaderTitle, & .MuiButton-text, & [data-testid="IndeterminateCheckBoxIcon"], & [data-testid="CheckBoxIcon"], & [data-testid="CheckBoxOutlineBlankIcon"]':
          {
            color: 'primary.main'
          },
          '& .MuiButton-text.Mui-disabled': {
            color: (theme: any) => theme.palette.text.disabled
          },
          '& .MuiDataGrid-overlayWrapperInner': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%'
          }
        }}
      />
    </>
  )
}

export default AgencyVerificationsTable;